import React, { useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { formatDateTime } from "../../../Helpers/Utils";
import tripUrls from "./tripUrls";

// Edit trip end time: for TEST customers only (Customer.testMode), while the trip is Ended. Moves the recorded
// end time so late hours, reminders and the fine charges can be tried without waiting. Only the end time
// changes; the late hours are worked out again from it. The server refuses any other customer.
// This file is the same in the admin and staff panels; only tripUrls differs.

// A Date -> the value a datetime-local input wants (local time, to the minute).
const toInput = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const WINDOW_MS = 24 * 60 * 60 * 1000; // at most 24 hours before or after the booked end

const EditEndTimeModal = ({ bookingId, endTime, dueAt, startTime, onClose, onSaved }) => {
    const [value, setValue] = useState(toInput(endTime));
    const [submitting, setSubmitting] = useState(false);
    const picked = value ? new Date(value) : null;
    const dueMs = dueAt ? new Date(dueAt).getTime() : NaN;
    const hasDue = Number.isFinite(dueMs);
    const within = !hasDue || (!!picked && Math.abs(picked.getTime() - dueMs) <= WINDOW_MS);
    const valid = !!picked && !Number.isNaN(picked.getTime()) && within && (!startTime || picked.getTime() > new Date(startTime).getTime());

    const submit = async () => {
        if (!valid) { toast.error("Pick an end time after the trip start and within 24 hours of the booked end"); return; }
        setSubmitting(true);
        const response = await Handler({ method: "patch", url: tripUrls.endTime(bookingId), data: { endTime: picked.toISOString() } });
        setSubmitting(false);
        if (response?.success) {
            toast.success("Trip end time updated");
            onSaved?.();
            onClose?.();
        } else {
            toast.error(response?.data?.message || "Could not update the end time");
        }
    };

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Trip End Time</h5>
                            <button type="button" className="btn-close" onClick={onClose} disabled={submitting}></button>
                        </div>
                        <div className="modal-body">
                            <p className="small text-muted">
                                Test customer only, for testing. This changes the recorded end time and works the late hours out again from it.
                                The new time must be within 24 hours before or after the booked end. Photos, readings and everything else stay as they are.
                            </p>
                            <p className="small mb-2">
                                Booked return: <strong>{dueAt ? formatDateTime(dueAt) : "-"}</strong><br />
                                Recorded end: <strong>{endTime ? formatDateTime(endTime) : "-"}</strong>
                            </p>
                            <label className="form-label small" htmlFor="test-end-time">New end time</label>
                            <input
                                id="test-end-time"
                                type="datetime-local"
                                className="form-control"
                                value={value}
                                min={hasDue ? toInput(dueMs - WINDOW_MS) : undefined}
                                max={hasDue ? toInput(dueMs + WINDOW_MS) : undefined}
                                onChange={(e) => setValue(e.target.value)}
                                disabled={submitting}
                            />
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
                            <button type="button" className="btn btn-warning" onClick={submit} disabled={submitting || !valid}>
                                {submitting ? "Saving..." : "Save End Time"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

export default EditEndTimeModal;
